import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import LabelIcon from "@mui/icons-material/Label";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getTiposContenidos } from "../../../helpers/TipoContenidosData";
import { getMetadatosWhereIdTipoContenidos } from "../../../helpers/MetadatoData";
import { ocrPlantillasListar } from "../../../helpers/OcrData";
import { RegionesEditor } from "./RegionesEditor";
import esArray from "src/general_components/functionEsArray";

export const StepConfigurar = ({ state, dispatch }) => {
  const [tipoContenidoList, setTipoContenidoList] = useState([]);
  const [plantillasList, setPlantillasList] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(true);
  const [loadingPlantillas, setLoadingPlantillas] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);

  useEffect(() => {
    const fetchTipos = async () => {
      try {
        // getTiposContenidos(auth=true) filters digitalizado=1
        const tipos = await getTiposContenidos(true);
        setTipoContenidoList(esArray(tipos));
      } catch {
        setTipoContenidoList([]);
      } finally {
        setLoadingTipos(false);
      }
    };
    const fetchPlantillas = async () => {
      setLoadingPlantillas(true);
      try {
        const list = await ocrPlantillasListar();
        setPlantillasList(Array.isArray(list) ? list : []);
      } catch {
        setPlantillasList([]);
      } finally {
        setLoadingPlantillas(false);
      }
    };
    fetchTipos();
    fetchPlantillas();
  }, []);

  const handleTipoContenidoChange = async (_event, value) => {
    if (!value) {
      dispatch({ type: "SET_TIPO_CONTENIDO", payload: { id_tipo_contenidos: null, id_ocr_plantilla: null, metadatosDefinition: [] } });
      return;
    }
    const parsedPlantillaId = parseInt(value.id_ocr_plantilla, 10);
    const id_ocr_plantilla = !isNaN(parsedPlantillaId) && parsedPlantillaId > 0 ? parsedPlantillaId : null;
    setLoadingMeta(true);
    let metadatosDefinition = [];
    try {
      const meta = await getMetadatosWhereIdTipoContenidos(value.id);
      metadatosDefinition = esArray(meta);
    } catch {
      metadatosDefinition = [];
    } finally {
      setLoadingMeta(false);
    }
    dispatch({
      type: "SET_TIPO_CONTENIDO",
      payload: { id_tipo_contenidos: value.id, id_ocr_plantilla, metadatosDefinition },
    });
  };

  const handlePlantillaChange = (_event, value) => {
    const parsedId = parseInt(value?.id, 10);
    dispatch({ type: "SET_PLANTILLA", payload: !isNaN(parsedId) && parsedId > 0 ? parsedId : null });
  };

  const NINGUNA = { id: null, nombre: "Ninguna", _ninguna: true };

  const selectedTipoContenido = tipoContenidoList.find((t) => String(t.id) === String(state.id_tipo_contenidos)) || null;
  const selectedPlantilla = state.id_ocr_plantilla
    ? (plantillasList.find((p) => parseInt(p.id, 10) === state.id_ocr_plantilla) || null)
    : NINGUNA;

  // ID de la plantilla vinculada al tipo de contenido seleccionado
  const autoPlantilla = (() => {
    const parsed = parseInt(selectedTipoContenido?.id_ocr_plantilla, 10);
    return !isNaN(parsed) && parsed > 0 ? parsed : null;
  })();
  const plantillaIsAuto = autoPlantilla !== null && autoPlantilla === state.id_ocr_plantilla;

  // Plantillas ordenadas: la vinculada al tipo primero, luego el resto alfabético
  const plantillasOrdenadas = [
    NINGUNA,
    ...[...plantillasList].sort((a, b) => {
      const aRel = parseInt(a.id, 10) === autoPlantilla;
      const bRel = parseInt(b.id, 10) === autoPlantilla;
      if (aRel && !bRel) return -1;
      if (!aRel && bRel) return 1;
      return (a.nombre || "").localeCompare(b.nombre || "");
    }),
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 680, mx: "auto" }}>
      {/* Step header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0A395F", mb: 0.5 }}>
          Configurar tipo de contenido
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecciona el tipo de contenido digitalizado y, opcionalmente, una plantilla OCR para extraer metadatos automaticamente.
        </Typography>
      </Box>

      {/* OCR toggle */}
      <Card
        variant="outlined"
        sx={{ mb: 3, borderRadius: 2, borderColor: state.useOcr ? "#1565C0" : "divider", backgroundColor: state.useOcr ? "#f0f7ff" : "#fafafa" }}
      >
        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 }, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Usar OCR
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {state.useOcr
                ? "Se extraeran metadatos automaticamente con reconocimiento optico"
                : "Los metadatos se completaran manualmente por contenido"}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={state.useOcr}
                onChange={(e) => dispatch({ type: "SET_USE_OCR", payload: e.target.checked })}
                color="primary"
              />
            }
            label=""
            sx={{ mr: 0 }}
          />
        </CardContent>
      </Card>

      {/* Info banner */}
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon />}
        sx={{ mb: 3, borderRadius: 2, backgroundColor: "#e3f2fd", color: "#0d47a1", "& .MuiAlert-icon": { color: "#1565C0" } }}
      >
        El tipo de contenido determina los metadatos que se asociaran a cada imagen. Si tiene una plantilla OCR vinculada, los valores se extraeran automaticamente.
      </Alert>

      {/* Tipo de contenido card */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, borderColor: state.id_tipo_contenidos ? "#1565C0" : "divider" }}>
        <CardContent sx={{ pb: "12px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                backgroundColor: "#e3f2fd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DescriptionIcon sx={{ color: "#1565C0", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Tipo de Contenido
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Solo tipos con digitalizacion habilitada
              </Typography>
            </Box>
          </Box>

          <Autocomplete
            options={tipoContenidoList}
            loading={loadingTipos}
            value={selectedTipoContenido}
            getOptionLabel={(opt) => `${opt.id} - ${opt.detalle}`}
            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
            onChange={handleTipoContenidoChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de contenido (digitalizado)"
                size="small"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingTipos ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Metadatos summary */}
          {loadingMeta && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Cargando metadatos...</Typography>
            </Box>
          )}

          {state.id_tipo_contenidos && !loadingMeta && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <LabelIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Metadatos del tipo ({state.metadatosDefinition?.length ?? 0} campos)
                </Typography>
              </Box>
              {state.metadatosDefinition?.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {state.metadatosDefinition.map((m) => (
                    <Chip
                      key={m.id}
                      label={m.label_form}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11, height: 22, borderColor: "#90caf9", color: "#1565C0" }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Sin metadatos definidos para este tipo.
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Plantilla OCR card — solo visible con OCR activo */}
      {state.useOcr && <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: state.id_ocr_plantilla ? "#2e7d32" : "divider",
        }}
      >
        <CardContent sx={{ pb: "12px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                backgroundColor: state.id_ocr_plantilla ? "#e8f5e9" : "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DocumentScannerIcon sx={{ color: state.id_ocr_plantilla ? "#2e7d32" : "#9e9e9e", fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Plantilla OCR
                </Typography>
                <Chip label="Opcional" size="small" sx={{ fontSize: 10, height: 18, backgroundColor: "#f5f5f5" }} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Extraccion automatica de metadatos por reconocimiento optico
              </Typography>
            </Box>
            {state.id_ocr_plantilla && (
              <CheckCircleOutlineIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
            )}
          </Box>

          {plantillaIsAuto && (
            <Alert severity="info" sx={{ mb: 2, py: 0.5, borderRadius: 1.5, fontSize: "0.8rem" }}>
              Plantilla asignada automaticamente por el tipo de contenido seleccionado.
            </Alert>
          )}

          <Autocomplete
            options={plantillasOrdenadas}
            loading={loadingPlantillas}
            value={selectedPlantilla}
            disableClearable
            getOptionLabel={(opt) => opt._ninguna ? "Ninguna" : `${opt.id} - ${opt.nombre || opt.detalle || opt.id}`}
            isOptionEqualToValue={(opt, val) => {
              if (opt._ninguna && val?._ninguna) return true;
              if (opt._ninguna || val?._ninguna) return false;
              return parseInt(opt.id, 10) === parseInt(val?.id, 10);
            }}
            groupBy={autoPlantilla
              ? (opt) => {
                  if (opt._ninguna) return "__ninguna__";
                  return parseInt(opt.id, 10) === autoPlantilla
                    ? "Vinculada al tipo de contenido"
                    : "Otras plantillas";
                }
              : undefined}
            renderGroup={(params) => {
              if (params.group === "__ninguna__") return <div key={params.key}>{params.children}</div>;
              return (
                <li key={params.key}>
                  <div style={{ position: "sticky", top: -8, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: "#1565C0", backgroundColor: "#e3f2fd", letterSpacing: 0.5 }}>
                    {params.group}
                  </div>
                  <ul style={{ padding: 0 }}>{params.children}</ul>
                </li>
              );
            }}
            onChange={handlePlantillaChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Plantilla OCR (opcional)"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPlantillas ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {!state.id_ocr_plantilla && (
            <Alert
              severity="warning"
              sx={{ mt: 2, py: 0.5, borderRadius: 1.5, fontSize: "0.8rem" }}
            >
              Sin plantilla OCR: el paso de extraccion sera omitido y los metadatos se completaran manualmente.
            </Alert>
          )}

          {state.id_ocr_plantilla && selectedPlantilla && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                icon={<DocumentScannerIcon />}
                label={`${selectedPlantilla.nombre || selectedPlantilla.detalle || `Plantilla #${state.id_ocr_plantilla}`}`}
                color="success"
                variant="outlined"
                size="small"
              />
            </Box>
          )}
        </CardContent>
      </Card>}

      {/* Preview de regiones readonly */}
      {state.useOcr && selectedPlantilla && !selectedPlantilla._ninguna && selectedPlantilla.imagen_referencia && selectedPlantilla.regiones?.length > 0 && (() => {
        const metaPreview = selectedPlantilla.regiones.map((r) => ({
          id: r.id_metadatos,
          label_form: r.label,
        }));
        return (
          <Card variant="outlined" sx={{ mt: 2, borderRadius: 2, borderColor: "divider" }}>
            <CardContent sx={{ pb: "12px !important" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0A395F", mb: 1 }}>
                Regiones de extraccion OCR
              </Typography>
              <RegionesEditor
                readonly
                metadatos={metaPreview}
                regiones={selectedPlantilla.regiones}
                imagenReferencia={selectedPlantilla.imagen_referencia}
                onChange={() => {}}
                onImagenChange={() => {}}
              />
            </CardContent>
          </Card>
        );
      })()}
    </Box>
  );
};
